import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, InputNumber, Switch, Tabs, Divider, App } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [generalForm] = Form.useForm();
  const [notificationForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('general');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const response = await api.get('/admin/settings');
      return response.data;
    }
  });

  const updateMutation = useMutation({
    mutationFn: (values: any) => api.post('/admin/settings', values),
    onSuccess: () => {
      message.success(t('settings.saved'));
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
    },
    onError: () => message.error(t('settings.saveError')),
  });

  // Set form values only when settings load and only for the active tab
  useEffect(() => {
    if (!settings) return;
    
    if (activeTab === 'general' && settings.general) {
      generalForm.setFieldsValue(settings.general);
    } else if (activeTab === 'notifications' && settings.notifications) {
      notificationForm.setFieldsValue(settings.notifications);
    } else if (activeTab === 'security' && settings.security) {
      securityForm.setFieldsValue(settings.security);
    }
  }, [settings, activeTab, generalForm, notificationForm, securityForm]);

  const handleGeneralSave = (values: any) => {
    updateMutation.mutate({ type: 'general', ...values });
  };

  const handleNotificationSave = (values: any) => {
    updateMutation.mutate({ type: 'notifications', ...values });
  };

  const handleSecuritySave = (values: any) => {
    updateMutation.mutate({ type: 'security', ...values });
  };

  const tabItems = [
    {
      key: 'general',
      label: t('settings.general'),
      children: (
        <Form form={generalForm} layout="vertical" onFinish={handleGeneralSave}>
          <Form.Item name="systemName" label={t('settings.systemName')}>
            <Input />
          </Form.Item>
          <Form.Item name="domain" label={t('settings.domain')}>
            <Input />
          </Form.Item>
          <Form.Item name="timeFormat" label={t('settings.timeFormat')}>
            <Input placeholder="24h lub 12h" />
          </Form.Item>
          <Form.Item name="timezone" label={t('settings.timezone')}>
            <Input placeholder="Europe/Warsaw" />
          </Form.Item>
          <Form.Item name="defaultEstimatedTime" label={t('settings.defaultEstimatedTime')}>
            <InputNumber min={1} max={60} />
          </Form.Item>
          <Form.Item name="minHistoryForEstimate" label={t('settings.minHistoryForEstimate')}>
            <InputNumber min={1} max={100} />
          </Form.Item>
          <Form.Item name="pickupDeadlineMinutes" label={t('settings.pickupDeadlineMinutes')}>
            <InputNumber min={1} max={30} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>
            {t('common.save')}
          </Button>
        </Form>
      ),
    },
    {
      key: 'notifications',
      label: t('settings.notifications'),
      children: (
        <Form form={notificationForm} layout="vertical" onFinish={handleNotificationSave}>
          <Form.Item name="pushEnabledClients" label={t('settings.enabledForClients')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="pushEnabledValets" label={t('settings.enabledForValets')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Divider>{t('settings.notificationAcceptedTitle')}</Divider>
          <Form.Item name="notificationRequestAcceptedTitle" label={t('settings.notificationAcceptedTitle')}>
            <Input />
          </Form.Item>
          <Form.Item name="notificationRequestAcceptedBody" label={t('settings.notificationAcceptedBody')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="notificationCarDeliveredTitle" label={t('settings.notificationDeliveredTitle')}>
            <Input />
          </Form.Item>
          <Form.Item name="notificationCarDeliveredBody" label={t('settings.notificationDeliveredBody')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>
            {t('common.save')}
          </Button>
        </Form>
      ),
    },
    {
      key: 'security',
      label: t('settings.security'),
      children: (
        <Form form={securityForm} layout="vertical" onFinish={handleSecuritySave}>
          <Form.Item name="refreshTokenExpiryHours" label={t('settings.refreshTokenExpiry')}>
            <InputNumber min={1} max={720} />
          </Form.Item>
          <Form.Item name="minPasswordLength" label={t('settings.minPasswordLength')}>
            <InputNumber min={6} max={32} />
          </Form.Item>
          <Form.Item name="requireUppercase" label={t('settings.requireUppercase')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="requireDigits" label={t('settings.requireDigits')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="requireSpecialChars" label={t('settings.requireSpecialChars')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>{t('common.save')}</Button>
        </Form>
      ),
    },
  ];

  return (
    <div>
      <h1>{t('settings.title')}</h1>
      <Card loading={isLoading}>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems} 
        />
      </Card>
    </div>
  );
};

export default SettingsPage;
